// pending-0.1.19 bump for RSC correction flow port
/**
 * CorrectionService (v3 RSC) — thin TS port of the SPFx
 * `CorrectionService.ts` (see `C:/Projects/retail-account-opening/
 * spfx-account-opening-v2/.../services/CorrectionService.ts`, read-only).
 *
 * The SPFx class constructed a direct `fetch()` wrapper against
 * `apiUrl + '/signing/correction'`. v3 has a shared `ApiHttpClient` (with
 * MSAL + x-functions-key plumbing + 503 handling) so we do NOT duplicate
 * the transport layer — instead we wrap `apiClient.signing.*` and layer the
 * correction-specific business logic on top:
 *
 *   - 3-attempt cap counting (per-signer, `[signer=<role>|<mode>]` reason stamp)
 *   - Pre-submit vs post-submit mode derivation from session status
 *   - Field-level correction payload construction
 *   - Event emission onto `/v3/events` so any watcher (polling or webhook
 *     relay) can react to `signing-corrected` / `signing-envelope-corrected`
 *
 * This intentionally stays small; the heavy lifting lives in
 * `SignNowCorrectionFunction.cs` server-side.
 */

import type { ApiClient } from '@retail-ops/api-client';
import type {
  CorrectionHistoryEntry,
  CorrectionHistoryResult,
  CorrectionRequestResult,
  CorrectSignerInput,
  CorrectSignerResult,
  SigningCorrectionType,
  SigningSignerInput,
} from '@retail-ops/shared-types';

/** Maps to `POST_SUBMIT_STATUSES` in CorrectionDrawer. */
const POST_SUBMIT_STATUSES: ReadonlySet<string> = new Set([
  'partial-signed',
  'all-signed',
  'completed',
  'complete',
  'in_progress',
  'dispatched',
]);

export type CorrectionMode = 'pre-submit' | 'post-submit';

export function deriveCorrectionMode(status: string | undefined): CorrectionMode {
  if (!status) return 'pre-submit';
  return POST_SUBMIT_STATUSES.has(status) ? 'post-submit' : 'pre-submit';
}

export interface CountForRoleOpts {
  sessionId: string;
  role: string;
}

export interface FieldCorrectionInput {
  sessionId: string;
  cartId?: string;
  groupId: string;
  documentIds: ReadonlyArray<string>;
  correctionType: SigningCorrectionType;
  reason: string;
  requestedBy: string;
  /** Stamp carried in the reason for later per-role counting. */
  signerRole?: string;
  mode: CorrectionMode;
}

export interface SignerReplacementInput {
  signingSessionId: string;
  oldSignerEmail: string;
  newSigner: SigningSignerInput;
  requestedBy: string;
  reason: string;
}

/**
 * CorrectionService — v3 RSC.
 *
 * Inject the shared `ApiClient` so MSAL + func-key headers flow through
 * automatically. Callers usually obtain this via `useApiClient()`.
 */
export class CorrectionService {
  public constructor(private readonly apiClient: ApiClient) {}

  /**
   * Count previously-submitted corrections scoped to a signer role by
   * scanning reason-text + Role fields for the `signer=<role>` stamp the UI
   * writes on submit.
   */
  public async countAttemptsForRole(opts: CountForRoleOpts): Promise<number> {
    const { sessionId, role } = opts;
    try {
      const hist = await this.apiClient.signing.listCorrectionsForSession(sessionId);
      const entries = extractEntries(hist);
      const roleL = role.toLowerCase();
      return entries.filter((e) => {
        const reasonL = (e.reason ?? '').toLowerCase();
        return reasonL.includes(`signer=${roleL}`);
      }).length;
    } catch {
      // 404/503 in dev — default to 0 so UI doesn't hard-fail.
      return 0;
    }
  }

  /**
   * Field-level correction — SignNow's 2-step "void + re-issue" flow. The
   * Function (`SignNowCorrectionFunction.cs`) performs the two jXRL-equivalent
   * calls (`POST /v2/document-groups/{dg}/correction` + `PUT /v2/document-groups-invites/{id}`);
   * this client just submits the intent.
   */
  public async requestFieldCorrection(
    input: FieldCorrectionInput,
  ): Promise<CorrectionRequestResult> {
    const reason = stampReason(input.reason, input.signerRole, input.mode);
    return this.apiClient.signing.requestCorrection({
      sessionId: input.sessionId,
      cartId: input.cartId,
      groupId: input.groupId,
      documentIds: input.documentIds,
      reason,
      correctionType: input.correctionType,
      requestedBy: input.requestedBy,
    });
  }

  /**
   * Signer-replacement correction (mid-session). Routes to `/signing/correction`
   * on the Function (see `packages/api-client/src/domains/signing.ts:196`).
   */
  public async replaceSigner(
    input: SignerReplacementInput,
    mode: CorrectionMode,
    role?: string,
  ): Promise<CorrectSignerResult> {
    const reason = stampReason(input.reason, role ?? input.newSigner.Role, mode);
    const payload: CorrectSignerInput = {
      signingSessionId: input.signingSessionId,
      oldSignerEmail: input.oldSignerEmail,
      newSigner: input.newSigner,
      requestedBy: input.requestedBy,
      reason,
    };
    return this.apiClient.signing.correctSigner(payload);
  }
}

/**
 * The Function may return the list under either `entries` (canonical) or
 * `Corrections` (SPFx-era shape). Normalize both.
 */
function extractEntries(
  hist: CorrectionHistoryResult | unknown,
): ReadonlyArray<CorrectionHistoryEntry> {
  if (!hist || typeof hist !== 'object') return [];
  const anyHist = hist as Record<string, unknown>;
  const entries = anyHist['entries'];
  if (Array.isArray(entries)) return entries as CorrectionHistoryEntry[];
  const corrections = anyHist['Corrections'];
  if (Array.isArray(corrections)) return corrections as CorrectionHistoryEntry[];
  return [];
}

/** `[signer=<role>|<mode>] <free-text>` */
function stampReason(reason: string, role: string | undefined, mode: CorrectionMode): string {
  const trimmed = reason.trim();
  const roleTag = (role ?? 'primary').toLowerCase();
  return `[signer=${roleTag}|${mode}] ${trimmed}`;
}
